import { ArrayItemType } from '@mm/utils';
import AudiencesModel from './AudiencesModel';
import { CampaignScheduleType } from './CampaignsModel';
import FBPostModel from './FBPostModel';

export type LinkTypes =
  | 'link'
  | 'instagram_link'
  | 'customer_chat'
  | 'checkbox'
  | 'send_to_messenger'
  | 'fb_comment_guard'
  | 'optin'
  | 'sms_keywords'
  | 'instagram_welcomer'
  | 'instagram_story_mention'
  | 'instagram_story_reaction'
  | 'instagram_comment_guard'
  | 'instagram_live_comment_guard'
  | 'instagram_catch_all'
  | 'facebook_catch_all'
  | 'getemails';

export const PromoterTypes = {
  LINK: 'link',
  INSTAGRAM_LINK: 'instagram_link',
  CUSTOMER_CHAT: 'customer_chat',
  CHECKBOX: 'checkbox',
  MESSENGER: 'send_to_messenger',
  FB_COMMENT_GUARD: 'fb_comment_guard',
  AWAY_RESPONDER: 'away_responder',
  OPTIN: 'optin',
  INSTAGRAM_WELCOMER: 'instagram_welcomer',
  INSTAGRAM_STORY_MENTION: 'instagram_story_mention',
  INSTAGRAM_STORY_REACTION: 'instagram_story_reaction',
  INSTAGRAM_COMMENT_GUARD: 'instagram_comment_guard',
  SMS_KEYWORDS: 'sms_keywords', // not promoter
  INSTAGRAM_LIVE_COMMENT_GUARD: 'instagram_live_comment_guard',
  INSTAGRAM_CATCH_ALL_ENGAGEMENTS: 'instagram_catch_all',
  FACEBOOK_CATCH_ALL_ENGAGEMENTS: 'facebook_catch_all',
  GETEMAILS: 'getemails',
};

export const GreetingDialogDisplayList = ['show', 'hide', 'fade'] as const;
export type GreetingDialogDisplay = ArrayItemType<
  typeof GreetingDialogDisplayList
>;
export const GreetingDialogDisplayCaptions: Record<
  GreetingDialogDisplay,
  string
> = {
  show: 'Always show greeting',
  hide: 'Always hide greeting',
  fade: 'Show greeting on initial page load, then hide',
};

export const PromoterConditionRuleOperatorCaptions = {
  '*': 'is any page',
  is: 'is',
  is_not: 'is not',
  contains: 'contains',
  does_not_contain: 'does not contain',
  starts_with: 'starts with',
  ends_with: 'ends with',
};
export type PromoterConditionRuleOperator =
  keyof typeof PromoterConditionRuleOperatorCaptions;

export const PromoterConditionRuleOperatorPriorities: Record<
  PromoterConditionRuleOperator,
  number
> = {
  is: 0,
  starts_with: 1,
  ends_with: 2,
  contains: 3,
  does_not_contain: 4,
  is_not: 5,
  '*': 6,
};

export type PromoterConditionRule = {
  operator: PromoterConditionRuleOperator;
  value: string;
};

export type PromoterConditions = {
  ruleMode: 'any' | 'all';
  rules: PromoterConditionRule[];
};

export const PromoterConversationContinueBehaviourConditionOperatorCaptions = {
  starts_with: 'starts with',
  contains: 'contains',
};
export type PromoterConversationContinueBehaviourConditionOperator =
  keyof typeof PromoterConversationContinueBehaviourConditionOperatorCaptions;
export interface PromoterConversationContinueBehaviour {
  mode:
    | 'resume'
    | 'always_greet'
    | 'conditional_resume'
    | 'active_search_token';
  condition?: {
    operator: PromoterConversationContinueBehaviourConditionOperator;
    value: string;
  };
  searchToken?: string;
}

const dateValue = (dateValue: string | undefined) =>
  new Date(dateValue || '').valueOf() || 0;
const promoterConditionValue = (promoter: PromoterModel) => {
  if (!promoter.conditions) {
    return PromoterConditionRuleOperatorPriorities['*'];
  }
  return Math.min(
    ...promoter.conditions.rules.map(
      rule => PromoterConditionRuleOperatorPriorities[rule.operator],
    ),
  );
};
export const sortPromotersByOperatorAndDateCreated = (
  promoters: PromoterModel[],
) =>
  promoters.sort((a, b) => {
    const operatorScoreA = promoterConditionValue(a);
    const operatorScoreB = promoterConditionValue(b);
    return operatorScoreA === operatorScoreB
      ? dateValue(b.createdAt) - dateValue(a.createdAt)
      : operatorScoreA - operatorScoreB;
  });

type PromoterShape = 'square' | 'circle';
export type PromoterPosition = 'bottom_right' | 'bottom_left';

export type PromoterCustomButton = {
  type: 'text' | 'direct_url' | 'phone_number';
  title: string;
  url?: string;
  payload?: string;
};

export type PromoterPublicReply = {
  id?: number;
  text: string;
  error?: string;
};

export const ErrorsList = error => {
  switch (error) {
    case 'SpamDetected':
      return 'This reply is marked as spam by Facebook and will not be sent. Please create multiple replies which will be sent at random.';
  }
};

export type Phrase = {
  id?: number;
  phrase: string;
  sortOrder: number;
  boosted?: boolean;
  createdAt: string;
  exactMatch?: boolean;
};

export type OptinSettings = {
  popupColor?: string;
  popupButtonColor?: string;
  popupTitle?: string;
  popupDescription?: string;
  popupImageUrl?: string | null;
  popupCtaSmsButtonText?: string;
  popupIncludesImage?: boolean;
  confirmationTitle?: string;
  confirmationDescription?: string;
  confirmationButtonText?: string;
};

export type XRaySettingsType =
  | 'consumer'
  | 'business'
  | 'business_and_consumer';

export enum XRayCustomRuleType {
  consumerData = 'consumer_data',
  leadAttribute = 'lead_attribute',
}

export type XRayCustomRule = {
  type: XRayCustomRuleType;
  value?: any;
  variableId?: number;
  variableType?: 'SystemVariable' | 'Variable';
};

export type PromoterSettings = {
  xray?: {
    leadType?: XRaySettingsType;
  };
  xrayCustomRulesMode?: 'all' | 'any';
  xrayCustomRules?: XRayCustomRule[];
};

export const optinSettingsDefault = {
  popupColor: '#E94D7B',
  popupButtonColor: '#4D58F4',
  popupTitle: 'Have a question about pricing?',
  popupDescription: "We're here to help.",
  popupCtaSmsButtonText: 'Submit',
  confirmationTitle: 'Thanks for reaching out!',
  confirmationDescription: `We'll get back shortly`,
  confirmationButtonText: 'Close',
};

export type PostId = 'ALL' | 'id' | string | undefined;

type EnabledOptins = 'sms';

export type GreetingStyle =
  | 'static'
  | 'inline_pic'
  | 'left_pic'
  | 'top_center_pic';

export type UsedInCampaignType = {
  id: number;
  name: string;
  scheduleType: CampaignScheduleType;
};

export type PromoterModel = {
  id?: number;
  name?: string;
  description: string;
  kind?: string;
  audiencesNames?: { id: number; name: string }[];
  audiences?: AudiencesModel[];
  createdAt?: string;
  updatedAt?: string;
  botId: number;
  type: LinkTypes;
  pageId: number;
  postId?: PostId;
  message?: string;
  active: boolean;
  activationDate?: string;
  postInfo?: FBPostModel;
  searchToken?: string;
  delayIntervalInMin?: number;
  loggedInGreeting?: string;
  loggedOutGreeting?: string;
  tokenLink?: string;
  slugLink?: string;
  slug?: string;
  color?: string;
  useGlobalJs?: boolean;
  hidden?: boolean;
  hideDesktop?: boolean;
  hideMobile?: boolean;
  conditions?: PromoterConditions;
  greetWithStartbot?: boolean;
  keywordsEnabled?: boolean;
  keywords?: string[];
  greetingDialogDisplay?: GreetingDialogDisplay;
  greetingDialogDisplayMobile?: GreetingDialogDisplay;
  greetingDialogDelay?: number;
  hideForNewVisitors?: boolean;
  hideForExistingVisitors?: boolean;
  optinSettings?: OptinSettings;
  enabledOptins?: EnabledOptins[];
  hideDuringOffHours?: boolean;
  shape?: PromoterShape;
  shadow?: boolean;
  position?: PromoterPosition;
  disableAnimation?: boolean;
  siteType?: string;
  siteUsesGtm?: boolean;
  webchatGreeting?: string;
  webchatGreetWithStartbot?: boolean;
  greetingStyle?: GreetingStyle;
  displayUserId?: number | null;
  customDisplayImageUrl?: string;
  customDisplayImageId?: number | null;
  customDisplayName?: string | null;
  customButtons?: PromoterCustomButton[] | null;
  customButtonsEnabled?: boolean;
  progressStep?: number | null;
  enableDataApi?: boolean;
  greetingInputPlaceholder?: string;
  webchatGreetingWidget?: any;
  conversationContinueBehaviour?: PromoterConversationContinueBehaviour;
  publicReplies?: PromoterPublicReply[];
  phrases?: Phrase[];
  sendPublicReply?: boolean;
  hidePageName?: boolean;
  sessionsStartedCount?: number;
  sendToDev?: string; // to strore email address
  scheduleGuided?: string; // to strore phone number or  email address
  usedInCampaigns?: UsedInCampaignType[];
  xrayReturningVisitorOnly?: boolean;
  xrayDelayEnabled?: boolean;
  xrayDelaySeconds?: number;
  settings?: PromoterSettings;
  connectionWebhookIds?: number[];
  xrayMinActivityEnabled?: boolean;
  xrayMinActivityCount?: number;
  xrayMinActivityType?: 'clicks' | 'views';
  visitCountsLast24H?: {
    promoter: number;
    total: number;
  };
};

export const isPromoter = (v: any): v is PromoterModel =>
  v?.id &&
  Object.values(PromoterTypes)
    .filter(v => v !== PromoterTypes.SMS_KEYWORDS)
    .includes(v.kind);
